import React, { useState, useEffect, useCallback, useRef } from "react";
import { X, TrashIcon, Loader2, Link2, Maximize2, Minimize2, Minus, CircleDashed, CheckCheck, ExternalLink, Sparkles } from "lucide-react";
import { TicketProps, TicketStatus, PRIORITY_LABELS, STATUS_LABELS, PRIORITY_TEXT_COLOR, SYNOPSIS_MODEL } from "../../../views/crm-pages/ticket/types";
import useRequest from "../../hook/use-req";
import { toast } from "sonner";
import useCrmApi from "../../hook/crm-req";
import Select from "../../select";
import { ISSUE_TYPE_OPTIONS } from "../customer/ticket-pop-up";

interface TicketDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    ticket: TicketProps | null;
    team: any[];
    currentUserId?: string | null;
    onTicketUpdated?: (updatedTicket: TicketProps) => void;
    onSynopsisGenerated?: (
        ticketId: string,
        fields: Pick<TicketProps, "synopsis" | "synopsis_generated_at" | "synopsis_model">
    ) => void;
}

const STATUS_OPTIONS: { value: TicketStatus; label: string; icon: React.ElementType }[] = [
    { value: "open", label: STATUS_LABELS.open, icon: Minus },
    { value: "pending", label: STATUS_LABELS.pending, icon: CircleDashed },
    { value: "closed", label: STATUS_LABELS.closed, icon: CheckCheck },
];

const STATUS_PILL: Record<TicketStatus, string> = {
    open: "bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400",
    pending: "bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400",
    closed: "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400",
};

const formatDate = (iso?: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

// The Field label/control pairing repeated throughout the drawer.
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div>
        <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-1.5">{label}</p>
        {children}
    </div>
);

const TicketDetailsModal: React.FC<TicketDetailsModalProps> = ({
    isOpen,
    onClose,
    ticket: initialTicket,
    team,
    currentUserId,
    onTicketUpdated,
    onSynopsisGenerated,
}) => {
    const token = localStorage.getItem("authToken");
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [ticket, setTicket] = useState<TicketProps | null>(null);
    const [expanded, setExpanded] = useState(false);
    const [statusSelectKey, setStatusSelectKey] = useState(0);
    const [generatingSynopsis, setGeneratingSynopsis] = useState(false);
    const [comments, setComments] = useState<any[]>([]);
    const [newComment, setNewComment] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingText, setEditingText] = useState("");
    const [attachments, setAttachments] = useState<File[]>([]);
    const [uploadedFileUrls, setUploadedFileUrls] = useState<string[]>([]);
    const [uploadingFiles, setUploadingFiles] = useState<{ [key: string]: boolean }>({});
    const [originalAttachmentUrls, setOriginalAttachmentUrls] = useState<string[]>([]);

    const { makeRequest: updateTicket } = useCrmApi(
        ticket ? `/tickets/${ticket.id}` : "",
        "PATCH",
        { Authorization: `Bearer ${token}` }
    );

    const { makeRequest: generateSynopsis } = useCrmApi(
        ticket ? `/tickets/${ticket.id}/synopsis` : "",
        "POST",
        { Authorization: `Bearer ${token}` }
    );

    const { makeRequest: uploadMedia } = useRequest("/media/upload", "POST", {
        Authorization: `Bearer ${token}`,
    });

    const uploadFile = async (file: File): Promise<string | null> => {
        const formData = new FormData();
        formData.append("files", file);

        try {
            const [response] = await uploadMedia(formData);
            if (response?.success) {
                return response.uploaded_files[0]?.preview_url;
            }
            return null;
        } catch (error) {
            console.error("Error uploading file:", error);
            return null;
        }
    };

    const handleFiles = useCallback(async (files: FileList | null) => {
        if (!files) return;
        const incoming = Array.from(files);

        for (const file of incoming) {
            setUploadingFiles(prev => ({ ...prev, [file.name]: true }));

            const uploadedUrl = await uploadFile(file);

            if (uploadedUrl) {
                setUploadedFileUrls(prev => [...prev, uploadedUrl]);
                setAttachments(prev => [...prev, file]);
                toast.success(`${file.name} uploaded successfully`);
            } else {
                toast.error(`Failed to upload ${file.name}`);
            }

            setUploadingFiles(prev => ({ ...prev, [file.name]: false }));
        }
    }, []);

    const removeAttachment = (index: number) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
        setUploadedFileUrls((prev) => prev.filter((_, i) => i !== index));
    };

    const removeExistingAttachment = (urlToRemove: string) => {
        setOriginalAttachmentUrls((prev) => prev.filter(url => url !== urlToRemove));
        setUploadedFileUrls((prev) => prev.filter(url => url !== urlToRemove));
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        handleFiles(e.dataTransfer.files);
    };

    const startEdit = (comment: any) => {
        setEditingId(comment.id);
        setEditingText(comment.body);
    };

    const saveEdit = (id: number) => {
        setComments((prev) =>
            prev.map((c) =>
                c.id === id ? { ...c, body: editingText } : c
            )
        );
        setEditingId(null);
        setEditingText("");
    };

    // API comments look like { body, created_at, author_name, author_type }
    // with no id of their own — index-based ids are assigned locally just
    // for React keys and this modal's own edit/delete UI.
    const formatCommentTime = (iso: string) => {
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditingText("");
    };

    const deleteComment = (id: number) => {
        setComments((prev) => prev.filter((c) => c.id !== id));
    };

    useEffect(() => {
        if (initialTicket) {
            setTicket(initialTicket);
            setExpanded(false);
            // Load existing attachments
            if (initialTicket.attachment_file_ids) {
                setUploadedFileUrls(initialTicket.attachment_file_ids);
                setOriginalAttachmentUrls(initialTicket.attachment_file_ids);
            }
            // Comments come back as a real array on the ticket itself —
            // this was never read into state before, so existing comments
            // (including ones from the customer) never showed up here.
            const rawComments = Array.isArray((initialTicket as any).comments) ? (initialTicket as any).comments : [];
            setComments(
                rawComments.map((c: any, idx: number) => ({
                    id: idx,
                    body: c.body ?? c.text ?? "",
                    author_name: c.author_name ?? c.user ?? "Unknown",
                    author_type: c.author_type ?? "agent",
                    created_at: c.created_at ?? c.time ?? "",
                }))
            );
        }
    }, [initialTicket]);

    const handleUpdate = async () => {
        if (!ticket) return;

        setIsSaving(true);
        try {
            // Fold in any comment still sitting in the input — clicking the
            // main Save button shouldn't silently drop a comment the user
            // typed but never separately submitted via the comment box's
            // own send button/Enter key.
            const pendingComment = newComment.trim();
            const allComments = pendingComment
                ? [
                    {
                        id: Date.now(),
                        author_name: ticket?.customer_name || "You",
                        author_type: "agent",
                        body: pendingComment,
                        created_at: new Date().toISOString(),
                    },
                    ...comments,
                ]
                : comments;

            // Prepare comments matching the API's own shape
            // ({ body, created_at, author_name, author_type }).
            const commentsData = allComments.map(comment => ({
                body: comment.body,
                author_name: comment.author_name,
                author_type: comment.author_type,
                created_at: comment.created_at,
            }));

            // Prepare payload - only include fields that should be updated
            const payload: any = {};

            // Only include status if changed
            if (ticket.status) {
                payload.status = ticket.status;
            }

            // Only include priority if changed
            if (ticket.priority) {
                payload.priority = ticket.priority;
            }

            // Only include issue_type if changed
            if (ticket.issue_type !== undefined) {
                payload.issue_type = ticket.issue_type;
            }

            // Only include issue_description if changed
            if (ticket.issue_description !== undefined) {
                payload.issue_description = ticket.issue_description;
            }

            // Only include assigned_to_user_id if changed
            if (ticket.assigned_to_user_id !== undefined) {
                payload.assigned_to_user_id = ticket.assigned_to_user_id;
            }

            // Include attachment URLs (all current URLs including existing and new ones)
            if (uploadedFileUrls.length > 0) {
                payload.attachment_file_ids = uploadedFileUrls;
            } else {
                payload.attachment_file_ids = [];
            }

            // Include comments if there are any
            if (commentsData.length > 0) {
                payload.comments = JSON.stringify(commentsData);
            }

            const [response] = await updateTicket(payload);
            const updated = response?.data || response;

            if (pendingComment) {
                setComments(allComments);
                setNewComment("");
            }

            toast.success("Ticket updated successfully");
            onTicketUpdated?.(updated);
            onClose();
        } catch (err) {
            console.error("Failed to update ticket:", err);
            toast.error("Failed to update ticket");
        } finally {
            setIsSaving(false);
        }
    };

    const addComment = () => {
        if (!newComment.trim()) return;
        const comment = {
            id: Date.now(),
            author_name: ticket?.customer_name || "You",
            author_type: "agent",
            body: newComment,
            created_at: new Date().toISOString(),
        };
        setComments((prev) => [comment, ...prev]);
        setNewComment("");
    };

    const isUploading = Object.values(uploadingFiles).some(status => status === true);
    const previewUrl = (file: File) => URL.createObjectURL(file);

    if (!isOpen || !ticket) return null;

    // POST /tickets/{id}/synopsis builds an AI summary from the ticket, its
    // comments and the latest linked conversation messages; calling it again
    // regenerates and replaces the stored one.
    const handleGenerateSynopsis = async () => {
        setGeneratingSynopsis(true);
        try {
            const [response, status] = await generateSynopsis({ model: SYNOPSIS_MODEL });
            const updated = response?.data || response;
            if (status >= 200 && status < 300 && typeof updated?.synopsis === "string") {
                const fields = {
                    synopsis: updated.synopsis,
                    synopsis_generated_at: updated.synopsis_generated_at ?? null,
                    synopsis_model: updated.synopsis_model ?? null,
                };
                setTicket((prev) => (prev ? { ...prev, ...fields } : prev));
                onSynopsisGenerated?.(ticket.id, fields);
                toast.success("Synopsis generated");
            } else {
                const detail = response?.detail;
                toast.error(typeof detail === "string" ? detail : "Failed to generate synopsis");
            }
        } catch {
            toast.error("Failed to generate synopsis");
        } finally {
            setGeneratingSynopsis(false);
        }
    };

    // Links to the public, read-only ticket view (routes/main-routes.tsx:
    // "ticketing/:ticketId" → views/crm-pages/ticket/single-ticket.tsx) —
    // the same page a customer would be sent, keyed by the ticket's real id.
    const ticketUrl = `${window.location.origin}/ticketing/${ticket.id}`;

    const copyLink = () => {
        navigator.clipboard.writeText(ticketUrl);
        toast.success("Link copied");
    };

    const assignedOn = formatDate(ticket.assigned_at);

    const issueTypeField = (
        <Field label="Issue Type">
            <Select
                value={ticket.issue_type || ""}
                onChange={(v) => setTicket({ ...ticket, issue_type: v })}
                options={ISSUE_TYPE_OPTIONS}
                placeholder="Select issue type"
            />
        </Field>
    );

    const assignedToField = (
        <Field label="Assigned to">
            <Select
                value={ticket.assigned_to_user_id || ""}
                onChange={(v) => setTicket({ ...ticket, assigned_to_user_id: v || null })}
                options={[
                    { value: "", label: "Unassigned" },
                    ...team.map((member) => ({
                        value: member.id,
                        label: currentUserId && member.id === currentUserId ? "Self" : `${member.first_name} ${member.last_name}`.trim(),
                    })),
                ]}
                placeholder="Unassigned"
            />
        </Field>
    );

    const handleStatusChange = (v: string) => {
        // A ticket can't be worked on by nobody — moving it to "In Progress"
        // requires an assignee first. The Select's own internal display state
        // updates optimistically on click regardless of what we do here, so
        // bumping statusSelectKey forces it to remount and re-read the
        // (unchanged) ticket.status instead of visually sticking on the
        // rejected value.
        if (v === "pending" && !ticket.assigned_to_user_id) {
            toast.error("Assign this ticket to someone before marking it In Progress.");
            setStatusSelectKey((k) => k + 1);
            return;
        }
        setTicket({ ...ticket, status: v as TicketStatus });
    };

    const statusField = (
        <Field label="Status">
            <Select key={statusSelectKey} value={ticket.status} onChange={handleStatusChange} options={STATUS_OPTIONS} />
        </Field>
    );

    const priorityField = (
        <Field label="Priority">
            <p className={`text-sm font-semibold ${PRIORITY_TEXT_COLOR[ticket.priority]}`}>{PRIORITY_LABELS[ticket.priority] || "Not set"}</p>
        </Field>
    );

    const descriptionField = (
        <div>
            <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-1.5">Issue Description</p>
            <textarea
                value={ticket.issue_description || ""}
                onChange={(e) => setTicket({ ...ticket, issue_description: e.target.value })}
                className="w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-300 resize-none focus:outline-none focus:border-indigo-500"
                rows={3}
                placeholder="No description"
            />
        </div>
    );

    const tagsField = ticket.tags?.length > 0 && (
        <div>
            <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-1.5">Ticket Tags</p>
            <div className="flex gap-2 flex-wrap">
                {ticket.tags.map((tag) => (
                    <span
                        key={tag}
                        className={`text-xs px-2 py-0.5 rounded-md ${
                            /lead/i.test(tag)
                                ? "bg-blue-50 dark:bg-blue-500/15 text-blue-600 dark:text-blue-400"
                                : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                        }`}
                    >
                        {tag}
                    </span>
                ))}
            </div>
        </div>
    );

    const attachmentsField = (
        <div>
            <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-2">Attachments</p>
            <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className="flex flex-wrap items-center gap-2"
            >
                {/* Existing attachments that weren't removed */}
                {originalAttachmentUrls.map((fileUrl, index) => (
                    <div key={`existing-${index}`} className="relative group w-20 h-20 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 shrink-0">
                        {fileUrl.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/) ? (
                            <img
                                src={fileUrl}
                                alt={`attachment-${index}`}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center text-center p-2">
                                <div className="text-2xl mb-1">
                                    {fileUrl.toLowerCase().endsWith(".pdf") ? "📄" : "📎"}
                                </div>
                                <span className="text-[10px] text-slate-500 dark:text-slate-400 break-all line-clamp-2">
                                    {fileUrl.split("/").pop()?.slice(0, 15)}
                                </span>
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => removeExistingAttachment(fileUrl)}
                            className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity"
                        >
                            <TrashIcon className="w-4 h-4" />
                        </button>
                    </div>
                ))}

                {/* Newly uploaded attachments */}
                {attachments.map((file, i) => (
                    <div key={`new-${i}`} className="relative group w-20 h-20 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 shrink-0">
                        {file.type.startsWith("image/") ? (
                            <img src={previewUrl(file)} alt={file.name} className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full bg-indigo-50 dark:bg-indigo-500/15 flex items-center justify-center text-indigo-600 dark:text-indigo-400 text-xs font-medium text-center px-1">
                                {file.name.split(".").pop()?.toUpperCase()}
                            </div>
                        )}
                        {uploadingFiles[file.name] && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                <Loader2 className="w-5 h-5 text-white animate-spin" />
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => removeAttachment(i)}
                            className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity"
                            disabled={uploadingFiles[file.name]}
                        >
                            <TrashIcon className="w-4 h-4" />
                        </button>
                    </div>
                ))}

                {uploadedFileUrls.length < 5 && !isUploading && (
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 underline underline-offset-2 transition-colors"
                    >
                        Upload Files
                    </button>
                )}
                {isUploading && (
                    <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">Uploading...</span>
                )}
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
            />
            <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-2">
                Upload up to 5 files (PNG, JPG, PDF, or DOC)
            </p>
        </div>
    );

    const openTicketUrlField = ticketUrl && (
        <a
            href={ticketUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 underline underline-offset-2"
        >
            Open Ticket URL <ExternalLink size={13} />
        </a>
    );

    const commentsSection = (
        <div>
            <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-2">{expanded ? "Ticket Comments" : "Comments"}</p>

            <div className="flex gap-2 mb-3">
                <input
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a new comment"
                    className="flex-1 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                    onKeyDown={(e) => e.key === "Enter" && addComment()}
                />
                <button
                    onClick={addComment}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
                >
                    Add
                </button>
            </div>

            <div className="space-y-3">
                {comments.map((c) => (
                    <div key={c.id} className="border-b border-gray-100 dark:border-gray-800 pb-3">
                        <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                            {c.author_name}
                            {c.author_type === "customer" && (
                                <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400">
                                    customer
                                </span>
                            )}{" "}
                            <span className="text-[#7B7B7B] font-semibold dark:text-gray-500">• {formatCommentTime(c.created_at)}</span>
                        </p>

                        {editingId === c.id ? (
                            <div className="mt-1">
                                <input
                                    value={editingText}
                                    onChange={(e) => setEditingText(e.target.value)}
                                    className="w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md px-2 py-1 text-sm"
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") saveEdit(c.id);
                                        if (e.key === "Escape") cancelEdit();
                                    }}
                                />
                                <div className="flex gap-3 mt-1 text-xs">
                                    <button
                                        onClick={() => saveEdit(c.id)}
                                        className="text-green-600 dark:text-green-400 font-medium"
                                    >
                                        Save
                                    </button>
                                    <button
                                        onClick={cancelEdit}
                                        className="text-gray-500 dark:text-gray-400"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <p className="text-sm text-gray-700 dark:text-gray-300 break-words">
                                    {c.body}
                                </p>
                                <div className="flex gap-3 text-[#4338CA] dark:text-indigo-400 underline text-xs mt-1">
                                    <button onClick={() => startEdit(c)}>Edit</button>
                                    <button onClick={() => deleteComment(c.id)}>Delete</button>
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

    const titleBlock = (
        <div className="px-6 pt-4 pb-1">
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100 leading-snug">
                {ticket.issue_description || ticket.issue_type || "Untitled ticket"}{" "}
                <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full align-middle ${STATUS_PILL[ticket.status]}`}>
                    • {STATUS_LABELS[ticket.status]}
                </span>
            </p>
        </div>
    );

    const synopsisBlock = (
        <div className="mx-6 mt-3 rounded-xl border border-primary-100 dark:border-primary-500/30 bg-primary-50/60 dark:bg-primary-500/10 px-4 py-3">
            <div className="flex items-center justify-between gap-3">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-primary-700 dark:text-primary-300">
                    <Sparkles size={13} /> Synopsis
                </p>
                <button
                    type="button"
                    onClick={handleGenerateSynopsis}
                    disabled={generatingSynopsis}
                    className="flex items-center gap-1 text-xs font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 disabled:opacity-60 transition-colors"
                >
                    {generatingSynopsis ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                    {ticket.synopsis ? "Regenerate" : "Generate"}
                </button>
            </div>
            {ticket.synopsis ? (
                <>
                    <p className="mt-1.5 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words">{ticket.synopsis}</p>
                    {ticket.synopsis_generated_at && (
                        <p className="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">
                            Generated {formatDate(ticket.synopsis_generated_at)}
                            {ticket.synopsis_model ? ` · ${ticket.synopsis_model}` : ""}
                        </p>
                    )}
                </>
            ) : (
                <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                    No synopsis yet — generate one from the ticket, its comments and recent conversation messages.
                </p>
            )}
        </div>
    );

    const headerActions = (
        <div className="flex items-center gap-3 shrink-0">
            <button onClick={copyLink} title="Copy ticket link" className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300">
                <Link2 size={17} />
            </button>
            <button
                onClick={() => setExpanded((v) => !v)}
                title={expanded ? "Collapse" : "Expand"}
                className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
            >
                {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
            <button onClick={onClose} className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300">
                <X size={20} />
            </button>
        </div>
    );

    const updateButton = (fullWidth: boolean) => (
        <button
            onClick={handleUpdate}
            disabled={isSaving || isUploading}
            className={`bg-indigo-600 text-white py-2.5 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${fullWidth ? "w-full" : "px-6"}`}
        >
            {isSaving ? "Updating..." : "Update Ticket"}
        </button>
    );

    const discardButton = (fullWidth: boolean) => (
        <button
            onClick={onClose}
            className={`border border-gray-300 dark:border-gray-700 py-2.5 rounded-lg text-sm text-gray-600 dark:text-gray-300 ${fullWidth ? "w-full" : "px-6"}`}
        >
            Discard Changes
        </button>
    );

    // Drawer stacks Update Ticket above Discard Changes, full width; the
    // expanded layout puts them side by side, right-aligned, Discard first.
    const drawerFooterActions = (
        <>
            {updateButton(true)}
            {discardButton(true)}
        </>
    );
    const expandedFooterActions = (
        <>
            {discardButton(false)}
            {updateButton(false)}
        </>
    );

    // Expanded (fullscreen) layout: a centered wide modal, ticket fields on
    // the left and Comments on its own column on the right — everything
    // else (state, handlers, field content) is shared with the drawer below.
    if (expanded) {
        return (
            <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
                <div className="absolute inset-0" onClick={onClose} />
                <div className="relative w-full max-w-6xl h-[85vh] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
                    <div className="flex items-center justify-between border-b dark:border-gray-800 px-6 py-4 shrink-0">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Ticket Details</h2>
                        {headerActions}
                    </div>
                    {titleBlock}
                    {synopsisBlock}
                    <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-2 gap-8 px-6 pb-4 pt-3">
                        <div className="overflow-y-auto pr-2 space-y-4 text-sm">
                            {issueTypeField}
                            {assignedToField}
                            {assignedOn && <Field label="Assigned On"><p className="text-sm text-gray-700 dark:text-gray-300">{assignedOn}</p></Field>}
                            {statusField}
                            {priorityField}
                            {descriptionField}
                            {tagsField}
                            {attachmentsField}
                            {openTicketUrlField}
                        </div>
                        <div className="overflow-y-auto pl-2 border-l border-gray-100 dark:border-gray-800 space-y-4 text-sm">
                            {commentsSection}
                        </div>
                    </div>
                    <div className="border-t dark:border-gray-800 px-6 py-4 flex items-center justify-end gap-2.5 shrink-0">
                        {expandedFooterActions}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`fixed inset-0 z-50 transition-all duration-300 ${isOpen ? "bg-black/40" : "bg-black/0 pointer-events-none"
                }`}
        >
            <div className="absolute inset-0" onClick={onClose} />

            <div
                className={`absolute top-0 right-0 h-full w-full sm:w-[520px] bg-white dark:bg-gray-900 shadow-2xl transition-transform duration-300 flex flex-col ${isOpen ? "translate-x-0" : "translate-x-full"
                    }`}
            >
                {/* Header */}
                <div className="flex items-center justify-between border-b dark:border-gray-800 px-6 py-4 shrink-0">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Ticket Details</h2>
                    {headerActions}
                </div>
                {titleBlock}
                {synopsisBlock}

                <div className="flex-1 overflow-auto px-6 py-4 space-y-4 text-sm">
                    {issueTypeField}
                    {assignedToField}
                    {assignedOn && <Field label="Assigned On"><p className="text-sm text-gray-700 dark:text-gray-300">{assignedOn}</p></Field>}
                    {statusField}
                    {priorityField}
                    {descriptionField}
                    {tagsField}
                    {attachmentsField}
                    {openTicketUrlField}
                    {commentsSection}
                </div>

                {/* Footer Actions */}
                <div className="border-t dark:border-gray-800 px-5 py-4 flex flex-col gap-2 shrink-0">
                    {drawerFooterActions}
                </div>
            </div>
        </div>
    );
};

export default TicketDetailsModal;
